import React, { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// PHASE-08 START
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary: 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-sm hover:shadow-md hover:from-primary/90 hover:to-primary/70',
        secondary: 'bg-secondary/10 text-secondary hover:bg-secondary/20 border border-secondary/20',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:shadow-md hover:bg-destructive/90',
        outline: 'border border-border bg-transparent hover:bg-muted/50 hover:border-muted-foreground/30',
        ghost: 'hover:bg-muted/50 hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline font-normal',
        glass: 'bg-white/10 dark:bg-muted/10 backdrop-blur-md border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-900/20',
        glow: 'bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-[0_0_20px_-5px] shadow-primary/50 hover:shadow-[0_0_30px_-5px] hover:shadow-primary/60',
      },
      size: {
        xs: 'h-8 px-3 text-xs rounded-md',
        sm: 'h-9 px-3 text-sm rounded-md',
        md: 'h-11 px-4 text-sm rounded-lg',
        lg: 'h-12 px-6 text-base rounded-lg',
        xl: 'h-14 px-8 text-lg rounded-xl',
        icon: 'h-11 w-11 rounded-lg',
        'icon-sm': 'h-9 w-9 rounded-md',
        'icon-xs': 'h-8 w-8 rounded-md',
      },
      rounded: {
        default: '',
        full: 'rounded-full',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'default',
    },
  }
);

const loadingVariants = cva(
  'absolute inset-0 flex items-center justify-center',
  {
    variants: {
      variant: {
        primary: '[&>div]:border-primary-foreground',
        secondary: '[&>div]:border-secondary',
        destructive: '[&>div]:border-destructive-foreground',
        outline: '[&>div]:border-current',
        ghost: '[&>div]:border-current',
        link: '[&>div]:border-current',
        glass: '[&>div]:border-current',
        glow: '[&>div]:border-primary-foreground',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, fullWidth, loading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(
          buttonVariants({ variant, size, rounded, className }),
          fullWidth && 'w-full',
          loading && 'relative'
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className={loadingVariants({ variant })}>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"></div>
          </div>
        )}
        <span className={cn(
          'inline-flex items-center gap-2',
          loading && 'opacity-0'
        )}>
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="shrink-0">{rightIcon}</span>}
        </span>
      </button>
    );
  }
);

Button.displayName = 'Button';
// PHASE-13 START
// Promotion shim: always use V2 while preserving legacy API.
import { ButtonV2 as __ButtonV2 } from './ButtonV2';

const ExportedButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { size, ...rest } = props as any;
  const mappedSize =
    size === 'xs' ? 'sm' :
    size === 'icon-xs' ? 'icon-sm' :
    size;
  return <__ButtonV2 ref={ref as any} size={mappedSize as any} {...(rest as any)} />;
});
ExportedButton.displayName = 'Button';
// PHASE-13 END

export { ExportedButton as Button, buttonVariants };
// PHASE-08 END
